import { Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../../RestockingState'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { useBlockPopAction } from 'src/hooks/useBlockPopAction'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import Page from 'src/features/restocking/components/RestockingWebAppForm/components/Page/Page'
import ScanCode from 'src/features/restocking/components/RestockingWebAppForm/components/ScanCode/ScanCode'
import TextLabel from 'src/features/restocking/components/RestockingWebAppForm/components/TextLabel/TextLabel'
import UserGuidance from 'src/features/restocking/components/RestockingWebAppForm/components/UserGuidance/UserGuidance'
import UserIdentifier from 'src/features/restocking/components/RestockingWebAppForm/components/UserIdentifier/UserIdentifier'
import theme from 'src/styles/theme'
import * as S from './CollectProductPage.styles'

interface FormData {
  barcode: string
}

const CollectProductPage: React.FC = () => {
  const [openDecisionConfirmationModal, setOpenDecisionConfirmationModal] = useState(false)
  const [openModalFullCart, setOpenModalFullCart] = useState(false)
  const { register, getValues, reset } = useForm<FormData>()
  const { currentCollectRestockingAddress, user } = useSelector((state) => state.restocking)
  const { fc } = useFulfillmentCenter()
  const history = useHistory()
  const dispatch = useDispatch()

  const closeConfirmationModal = () => setOpenDecisionConfirmationModal(false)
  const closeModalFullCart = () => setOpenModalFullCart(false)

  useBlockPopAction()

  useEffect(() => {
    if (!currentCollectRestockingAddress) {
      const surplusAddress = user.startedPage.itemsToCollect?.addresses.find((address) => !address.done)

      if (!surplusAddress) return

      dispatch(actions.setProperty({
        property: 'currentCollectRestockingAddress',
        value: surplusAddress,
      }))
    }
  }, [currentCollectRestockingAddress, dispatch, user])

  useEffect(() => {
    if (currentCollectRestockingAddress?.done) {
      dispatch(actions.clearProperty('currentCollectRestockingAddress'))

      const hasSourceAddress = user.startedPage.itemsToCollect?.addresses.find(
        (address) =>
          !address.done && address.order !== currentCollectRestockingAddress.order,
      )

      if (hasSourceAddress) {
        dispatch(actions.setProperty({
          property: 'currentCollectRestockingAddress',
          value: hasSourceAddress,
        }))

        history.push('/restocking/webapp/collect-address')
      }
    }
  }, [currentCollectRestockingAddress, history, user, dispatch])

  const currentProduct = currentCollectRestockingAddress?.items.find((item) => !item.done)

  useEffect(() => {
    if (!currentProduct && currentCollectRestockingAddress) {
      dispatch(actions.setRestockingAddressIsDone(currentCollectRestockingAddress.order))
    }
  }, [currentProduct, currentCollectRestockingAddress, dispatch])

  const navigateToBatchCollect = () => history.push('/restocking/webapp/collect-batch')

  const onSuccess = () => navigateToBatchCollect()

  const onSubmit = () => {
    if (!currentProduct || !user.startedPage.itemsToCollect) return

    const barcode = getValues('barcode').trim()

    dispatch(actions.restockingValidateProduct({
      badge: user.badge,
      idPage: user.startedPage.itemsToCollect?.id,
      idModel: currentProduct.idModel,
      productBarcode: barcode,
      onSuccess,
    }))

    reset({ barcode: '' })
  }

  const onSubmitProductNotFound = () => {
    if (!currentProduct || !user.startedPage.itemsToCollect || !currentCollectRestockingAddress) return

    const idsItemAddress = currentCollectRestockingAddress.items.flatMap((item) => {
      if (item.idModel === currentProduct.idModel) return item.itemAddress.ids
      return []
    })

    const notFoundValue = -1

    const itemAddress = {
      idItem: currentProduct.id,
      idsItemAddress,
      quantity: notFoundValue,
      quantityToCollect: currentProduct.itemAddress.quantity,
    }

    dispatch(actions.finishRestockingAddress({
      itemAddress,
      badge: user.badge,
      idPage: user.startedPage.itemsToCollect.id,
      idFulfillmentCenter: fc.id,
      addressOrder: currentCollectRestockingAddress.order,
      idModel: currentProduct.idModel,
      history,
    }))

    dispatch(actions.updateAddressOnProductNotFound(currentCollectRestockingAddress.order))

    closeConfirmationModal()
  }

  const onSubmitFullCart = () => {
    if (!user.startedPage.itemsToCollect) return

    dispatch(actions.restockingFullcart({
      badge: user.badge,
      idPage: user.startedPage.itemsToCollect.id,
      history,
      idsItemAddress: [],
    }))

    setOpenModalFullCart(false)
  }

  return (
    <Page headerTitle='Reposição' hasBackButton={false}>
      <S.Info>
        <UserIdentifier idPage={user.startedPage.itemsToCollect?.id}/>
        <UserGuidance>
          <TextLabel
            width='100%'
            height='3.2rem'
            backgroundColor={theme.colors.green4}
          >
            <Typography style={{ fontWeight: 'bold', fontSize: '30px' }}>
              {currentCollectRestockingAddress?.address}
            </Typography>
          </TextLabel>
        </UserGuidance>
        <S.ContainerProductName>
          <Typography style={{ fontWeight: 'bold', fontSize: '36px' }}>COLETAR</Typography>
          <UserGuidance>
            <TextLabel
              width='100%'
              height='3.2rem'
            >
              <Typography style={{ fontWeight: 'bold', fontSize: '13px' }}>
                {currentProduct?.description}
              </Typography>
            </TextLabel>
          </UserGuidance>

        </S.ContainerProductName>
      </S.Info>
      <S.Form>
        <ScanCode
          text='Ao localizar o produto bipe o código de barras.'
          onSubmit={onSubmit}
          register={register}
          dataTestId='input-barcode-test-id'
          inputName='barcode'
          errorMessage='Código de barras inválido!'
        />
        <S.ContainerButton>
          <S.CustomButton
            title='Não encontrei o produto'
            bgColor='cancel'
            hoverColor='cancelHover'
            letterColor='white'
            data-testid='button-not-found-product-test-id'
            onClick={() => setOpenDecisionConfirmationModal(true)}
          />
          <S.CustomButton
            title='Carrinho cheio'
            bgColor='orange2'
            hoverColor='orange'
            letterColor='white'
            data-testid='full-cart-button-test-id'
            onClick={() => setOpenModalFullCart(true)}
          />
        </S.ContainerButton>
      </S.Form>
      <ConfirmOrRejectModal
        show={openDecisionConfirmationModal}
        title='Confirmação de decisão'
        description='Deseja confirmar que não encontrou o produto?'
        onConfirm={onSubmitProductNotFound}
        onReject={closeConfirmationModal}
        onClose={closeConfirmationModal}
        dataTestId='modal-confirmation-not-found-product-test-id'
      />
      <ConfirmOrRejectModal
        show={openModalFullCart}
        title='Confirmação carrinho cheio'
        description='Confirme que o carrinho está cheio.'
        onConfirm={onSubmitFullCart}
        onReject={closeModalFullCart}
        onClose={closeModalFullCart}
        dataTestId='modal-confirmation-full-cart-test-id'
      />
    </Page>
  )
}

export default CollectProductPage
